import { useState } from 'react';
import {
  Button,
  Dropdown,
  Nav,
  Navbar,
  NavDropdown,
  Offcanvas,
} from 'react-bootstrap';
import { useLocation } from 'react-router-dom';

import { faBullhorn } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import optainLogoImg from 'src/assets/icons/logo/optain.png';

import useRemoteBoundStore from 'src/store/useRemoteBoundStore';

import CameraConnectivity from 'src/components/cameraConnectivity';
import ChangePwdModal from 'src/components/custom_components/ChangePwdModal';

import { Api } from 'src/models/Api.model';
import { getLocaleCode, Language } from 'src/models/Language.model';

import useCheckNavigation from 'src/hooks/useCheckNavigation';
import { useCheckNavItemVisibility } from 'src/hooks/useCheckNavItemVisibility';
import useUiTranslation from 'src/hooks/useUiTranslation';

import formatUserRole from 'src/utils/formatUserRole';

import UiLanguageDropdown from '../UiLanguageDropdown';

const Header = () => {
  const [showChangePassword, setShowChangePassword] = useState(false);

  const handlePwdOpen = () => setShowChangePassword(true);
  const handlePwdClose = () => setShowChangePassword(false);

  const { currentUser } = useRemoteBoundStore();
  const { checkNavigation } = useCheckNavigation();

  const userType = currentUser?.currentRole?.role;

  const fullName = currentUser?.fullName;

  const { tCommon, tPages, language } = useUiTranslation();

  const {
    logOut,
    cameraData,
    practiceSettings: { broadcast },
  } = useRemoteBoundStore();

  const handleLogout = () => {
    Api.confirmBox({
      title: tCommon('bootbox.title.Confirm'),
      message: tPages('loginLogout.confirmLogout'),
      callback: (result: boolean) => {
        if (!result) {
          return;
        }

        logOut();
      },
      locale: getLocaleCode(language as Language),
    });
  };

  const location = useLocation();

  const { isShowNewExamPage, isShowResultsPage, isShowUsersPage } =
    useCheckNavItemVisibility();

  return (
    <>
      <Navbar key="lg" expand="lg">
        <Navbar.Brand href="#">
          <div style={{ height: '58px' }}>
            <img
              src={optainLogoImg}
              alt="Logo"
              className="mh-100 object-fit-contain"
            />
          </div>
        </Navbar.Brand>

        {cameraData && <CameraConnectivity />}

        {broadcast && (
          <Button variant="warning" className="ms-3">
            <FontAwesomeIcon icon={faBullhorn} className="me-2" />
            {broadcast}
          </Button>
        )}

        <Navbar.Toggle aria-controls="basic-navbar-nav" />

        <Navbar.Offcanvas
          id="basic-navbar-nav"
          aria-labelledby="basic-navbar-nav-expand"
          placement="end"
        >
          <Offcanvas.Header closeButton>
            <Offcanvas.Title id="basic-navbar-nav-expand">
              <h2>Optain</h2>
            </Offcanvas.Title>
          </Offcanvas.Header>

          <Offcanvas.Body>
            <Nav className="justify-content-end flex-grow-1 ps-3 gap-2">
              {isShowNewExamPage && (
                <Nav.Link
                  href="#/session-form"
                  className="d-md-block d-lg-none text-start border text-black"
                  as={Button}
                  variant="light"
                  onClick={checkNavigation}
                >
                  {tCommon('tabs.newExam')}
                </Nav.Link>
              )}

              {isShowResultsPage && (
                <Nav.Link
                  href="#/results"
                  className="d-md-block d-lg-none text-start border text-black"
                  as={Button}
                  variant="light"
                  onClick={checkNavigation}
                >
                  {tCommon('tabs.results')}
                </Nav.Link>
              )}

              <Nav.Link
                href="#/settings"
                className="d-md-block d-lg-none text-start border text-black"
                as={Button}
                variant="light"
                onClick={checkNavigation}
              >
                {tCommon('tabs.settings')}
              </Nav.Link>

              {isShowUsersPage && (
                <Nav.Link
                  href="#/subuser"
                  className="d-md-block d-lg-none text-start border text-black"
                  as={Button}
                  variant="light"
                  onClick={checkNavigation}
                >
                  {tCommon('tabs.users')}
                </Nav.Link>
              )}

              <Nav.Link
                href="#/about"
                active={location.pathname === '/about'}
                className="d-md-block text-start border text-black"
                onClick={checkNavigation}
                as={Button}
                variant="light"
              >
                {tCommon('tabs.about')}
              </Nav.Link>

              <Dropdown align="end">
                <Dropdown.Toggle
                  variant="outline-light"
                  className="w-100 border border-1 text-start"
                >
                  <>
                    <span className="text-black fw-medium">
                      {userType
                        ? tCommon(
                            `patientFields.userRoleEnum.${formatUserRole(userType)}`,
                          )
                        : tPages(`users.user`)}
                      :
                    </span>
                    <span className="ms-2 text-black text-truncate">
                      {fullName}
                    </span>
                  </>
                </Dropdown.Toggle>

                <Dropdown.Menu>
                  <NavDropdown.Item
                    as="div"
                    onClick={(e) => e.stopPropagation()}
                  >
                    <UiLanguageDropdown className="text-black" />
                  </NavDropdown.Item>

                  <NavDropdown.Divider />
                  <NavDropdown.Item onClick={handlePwdOpen}>
                    {tPages('changePassword.title')}
                  </NavDropdown.Item>
                  <NavDropdown.Divider />
                  <NavDropdown.Item onClick={handleLogout}>
                    {tPages('loginLogout.logout')}
                  </NavDropdown.Item>
                </Dropdown.Menu>
              </Dropdown>
            </Nav>
          </Offcanvas.Body>
        </Navbar.Offcanvas>
      </Navbar>
      <ChangePwdModal
        isShowChangePwdModal={showChangePassword}
        handleCloseChangePwdModal={handlePwdClose}
        canBeCanceled
      />
    </>
  );
};

export default Header;
